import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
	CustomDetailCategory,
	DocumentTemplateStatus,
	DocumentWorkflowTemplateVisibleIn,
	TemplateAvailability,
} from '@dealstack/shared/lib/generated';
import { useTemplateAvailabilityQuery } from '@dealstack/frontend/app/pages/DocumentTemplates/api/useTemplateAvailabilityQuery';
import { useUpdateTemplateAvailability } from '@dealstack/frontend/app/pages/DocumentTemplates/api/useUpdateTemplateAvailability';
import { useDocumentTemplateQuery } from '@dealstack/frontend/app/pages/DocumentTemplates/api/useDocumentTemplateQuery';
import { useUpdateDocumentTemplate } from '@dealstack/frontend/app/pages/DocumentTemplates/api/useUpdateDocumentTemplate';
import type {
	TemplateAvailabilityFormValues,
	UseTemplateAvailabilityFormArgs,
} from './useTemplateAvailabilityForm.types';

const schema = z.object({
	status: z.nativeEnum(DocumentTemplateStatus),
	availability: z.nativeEnum(TemplateAvailability),
	dealIds: z.array(z.string()),
	kycAudience: z.array(z.nativeEnum(DocumentWorkflowTemplateVisibleIn)),
	kycCategory: z.nativeEnum(CustomDetailCategory).nullable(),
});

/**
 * Status and availability together: both answer "who can use this", and only a PUBLISHED
 * template reaches a deal whatever its availability says.
 */
export const useTemplateAvailabilityForm = ({
	templateId,
	onSaved,
}: UseTemplateAvailabilityFormArgs) => {
	const { data: availabilityData, isLoading: isLoadingAvailability } =
		useTemplateAvailabilityQuery(templateId);
	const { data: template, isLoading: isLoadingTemplate } = useDocumentTemplateQuery(templateId);
	const { mutateAsync: saveAvailability, isPending: isSavingAvailability } =
		useUpdateTemplateAvailability();
	const { mutateAsync: saveTemplate, isPending: isSavingTemplate } = useUpdateDocumentTemplate();

	const storedStatus = template?.status ?? DocumentTemplateStatus.DRAFT;

	const { control, handleSubmit, watch, setValue } = useForm<TemplateAvailabilityFormValues>({
		resolver: zodResolver(schema),
		// `values` keeps the form in step with the queries without a useEffect.
		values: {
			status: storedStatus,
			availability: availabilityData?.availability ?? TemplateAvailability.SELECTED_DEALS,
			dealIds: availabilityData?.dealIds ?? [],
			kycAudience: template?.kycAudience ?? [],
			kycCategory: template?.kycCategory ?? null,
		},
	});

	const storedAudience = template?.kycAudience ?? [];
	const storedCategory = template?.kycCategory ?? null;

	const submit = handleSubmit(async ({ status, kycAudience, kycCategory, ...availability }) => {
		// Two endpoints, so the unchanged half is skipped rather than re-sent — otherwise an
		// ordinary availability edit fires a second write and a second toast for nothing.
		const tagChanged =
			kycCategory !== storedCategory ||
			kycAudience.length !== storedAudience.length ||
			kycAudience.some((audience) => !storedAudience.includes(audience));
		if (status !== storedStatus || tagChanged) {
			await saveTemplate({
				id: templateId,
				// The server drops the category when the audience empties, so an untagged
				// template is sent as the pair rather than relying on the local null.
				body: { status, kycAudience, kycCategory },
			});
		}
		await saveAvailability({ id: templateId, body: availability });
		onSaved();
	});

	return {
		control,
		handleSubmit: submit,
		status: watch('status'),
		setStatus: (status: DocumentTemplateStatus) => setValue('status', status),
		availability: watch('availability'),
		kycAudience: watch('kycAudience'),
		setKycAudience: (kycAudience: DocumentWorkflowTemplateVisibleIn[]) =>
			setValue('kycAudience', kycAudience),
		kycCategory: watch('kycCategory'),
		setKycCategory: (kycCategory: CustomDetailCategory | null) =>
			setValue('kycCategory', kycCategory),
		// A mode change never clears dealIds: the server persists the selection in both modes, so
		// switching back restores it.
		setAvailability: (availability: TemplateAvailability) =>
			setValue('availability', availability),
		isLoading: isLoadingAvailability || isLoadingTemplate,
		isSaving: isSavingAvailability || isSavingTemplate,
	};
};
